import React, { useEffect } from 'react'
import styled from 'styled-components'
import { setViewConfirmModal } from 'features/modal/modalSlice'
import { useDispatch } from 'react-redux'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import SpinnerSmall from 'components/SpinnerSmall'

const getConfirmLabel = (text = '') => {
  const normalizedText = text.toLowerCase()
  if(normalizedText.includes('delete')) return 'Delete'
  if(normalizedText.includes('remove')) return 'Remove'
  if(normalizedText.includes('leave')) return 'Leave'
  if(normalizedText.includes('cancel')) return 'Cancel'
  if(normalizedText.includes('end session')) return 'End Session'
  return 'Confirm'
}

export default function ConfirmModal({
  text,
  handler,
  hasCloseHandler,
  closeHandler,
  doSomethingAfterClose,
  extraInfo,
  conditionToCloseModal,
  resetAfterClose,
  loadingState,
}) {
  const dispatch = useDispatch()
  const confirmLabel = getConfirmLabel(text)
  const destructiveAction = /delete|remove|leave|cancel|end session/i.test(text || '')

  const handleClose = () => {
    if(loadingState) return
    if(hasCloseHandler && closeHandler) closeHandler()
    dispatch(setViewConfirmModal(false))
  }

  useEffect(() => {
    if(conditionToCloseModal) {
      if(doSomethingAfterClose && resetAfterClose) resetAfterClose()
      dispatch(setViewConfirmModal(false))
    }
  }, [conditionToCloseModal, doSomethingAfterClose, dispatch, resetAfterClose])

  return (
    <ConfirmCard role="dialog" aria-modal="true" aria-labelledby="legacy-confirm-title">
      <ConfirmIcon><WarningAmberRoundedIcon /></ConfirmIcon>
      <ConfirmTitle id="legacy-confirm-title">{text || 'Are you sure?'}</ConfirmTitle>
      <ConfirmDescription>
        {extraInfo || 'This action may not be reversible. Please confirm that you want to continue.'}
      </ConfirmDescription>
      <ConfirmActions>
        <CancelButton type="button" disabled={loadingState} onClick={handleClose}>Cancel</CancelButton>
        <ActionButton type="button" disabled={loadingState} onClick={handler} $destructive={destructiveAction}>
          {loadingState ? <SpinnerSmall /> : <>{destructiveAction && <DeleteOutlineRoundedIcon />} {confirmLabel}</>}
        </ActionButton>
      </ConfirmActions>
    </ConfirmCard>
  )
}

const ConfirmCard = styled.section`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(430px, calc(100vw - 36px));
  padding: 24px;
  border: 1px solid #e2e7e9;
  border-radius: 18px;
  background: #fff;
  color: #344145;
  box-shadow: 0 24px 70px rgba(16, 31, 36, .24);
  text-align: center;
  animation: animate .2s ease-out;
`

const ConfirmIcon = styled.span`
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  margin: 0 auto;
  border-radius: 13px;
  background: #fff0ee;
  color: #b23f35;
  svg { font-size: 25px; }
`

const ConfirmTitle = styled.h2`
  margin: 12px 0 7px;
  font-size: 18px;
  line-height: 1.35;
`

const ConfirmDescription = styled.p`
  margin: 0;
  color: #748084;
  font-size: 12px;
  line-height: 1.55;
`

const ConfirmActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 20px;
`

const ConfirmButton = styled.button`
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 750;
  &:disabled { opacity: .65; cursor: default; }
`

const CancelButton = styled(ConfirmButton)`
  border: 1px solid #d9e0e2;
  background: #fff;
  color: #4e5b5f;
  &:hover:not(:disabled) { background: #f1f4f5; }
`

const ActionButton = styled(ConfirmButton)`
  border: 1px solid ${props => props.$destructive ? '#b23f35' : 'var(--themeColor)'};
  background: ${props => props.$destructive ? '#b23f35' : 'var(--themeColor)'};
  color: #fff;
  svg { font-size: 17px; }
  &:hover:not(:disabled) { background: ${props => props.$destructive ? '#96332b' : 'var(--darkThemeColor, #168fa1)'}; }
`
